import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FolderIcon from '@mui/icons-material/Folder';

/**
 * Modal component to display all project contacts for a client
 * Shows detailed information about each contact and their associated projects
 */
const ClientProjectContactsModal = ({ 
  open, 
  onClose, 
  projectContacts = [],
  clientName = '',
  totalProjects = 0
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0A0B0F',
          backgroundImage: 'none',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            Client Contacts - {clientName}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
            {projectContacts.length} unique contacts across {totalProjects} projects
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {projectContacts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              No project contacts found
            </Typography>
          </Box>
        ) : (
          <List sx={{ width: '100%' }}>
            {projectContacts.map((contactInfo, index) => {
              const contact = contactInfo.contact || {};
              const projectNames = contactInfo.project_names || [];
              const isPrimary = contactInfo.is_primary || false;
              
              return (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start" sx={{ px: 0, py: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      <PersonIcon sx={{ color: isPrimary ? 'primary.main' : 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography component="span" variant="body1" sx={{ fontWeight: 'medium' }}>
                            {contact.name || 'Unknown Contact'}
                          </Typography>
                          {isPrimary && (
                            <Chip 
                              label="PRIMARY" 
                              size="small" 
                              color="primary"
                              sx={{ 
                                fontSize: '0.65rem', 
                                height: '18px'
                              }} 
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {/* Title */}
                          {contact.title && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              {contact.title}
                            </Typography>
                          )}
                          
                          {/* Contact Details */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                            {contact.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {contact.email}
                                </Typography>
                              </Box>
                            )}
                            
                            {contact.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {contact.phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          {/* Associated Projects */}
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <FolderIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.8rem', 
                                color: 'text.secondary',
                                fontWeight: 'medium'
                              }}>
                                Associated Projects ({projectNames.length}):
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, ml: 3 }}>
                              {projectNames.map((projectName, projectIndex) => (
                                <Chip
                                  key={projectIndex}
                                  label={projectName}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    borderColor: 'divider'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < projectContacts.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

ClientProjectContactsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectContacts: PropTypes.arrayOf(PropTypes.shape({
    contact: PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string
    }),
    is_primary: PropTypes.bool,
    projects: PropTypes.arrayOf(PropTypes.string),
    project_names: PropTypes.arrayOf(PropTypes.string),
    project_count: PropTypes.number
  })),
  clientName: PropTypes.string,
  totalProjects: PropTypes.number
};

ClientProjectContactsModal.defaultProps = {
  projectContacts: [],
  clientName: '',
  totalProjects: 0
};

export default ClientProjectContactsModal;
